import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Toast, Popconfirm, Avatar, Typography } from '@douyinfe/semi-ui';
const { Text } = Typography;
import { IconEdit, IconDelete, IconStop, IconPlayCircle, IconPlus, IconSearch, IconEyeOpened } from '@douyinfe/semi-icons';
import { useNavigate } from 'react-router-dom';
import request from '../../utils/request';
import styles from './index.module.scss';

const Users = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [searchStatus, setSearchStatus] = useState(null);

  const statusMap = {
    0: { text: '未审核', type: 'warning' },
    1: { text: '正常', type: 'success' },
    2: { text: '审核失败', type: 'danger' },
    20: { text: '封号', type: 'danger' },
    60: { text: '异常锁定', type: 'danger' }
  };

  const genderMap = {
    1: '男',
    2: '女'
  };

  const fetchList = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await request.get('/go/sp/su/list_service_user/', {
        params: {
          page: page - 1,
          limit,
          key: searchKey,
          status: searchStatus === null ? -1 : searchStatus
        }
      });
      if (response.errno === 0) {
        setDataSource(response.list || []);
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          total: response.total_cnt
        }));
      } else {
        Toast.error(response.errstr || '获取列表失败');
      }
    } catch (error) {
      console.error('获取列表失败:', error);
      Toast.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleAdd = async (values) => {
    // 数据预处理
    const processedValues = {
      ...values,
      // 确保数值类型字段为整数
      gender: parseInt(values.gender),
      status: parseInt(values.status),
      from_su_uid: values.from_su_uid ? parseInt(values.from_su_uid) : '',
      // 处理生日格式，确保日期格式有效
      birthday: values.birthday ? (() => {
        const dateObj = new Date(values.birthday);
        if (isNaN(dateObj.getTime())) return 0;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return parseInt(`${year}${month}${day}`);
      })() : 0
    };

    try {
      const response = await request.post('/go/sp/su/add_service_user/', processedValues);
      if (response.errno === 0) {
        Toast.success('添加成功');
        setShowAddModal(false);
        fetchList();
      } else {
        Toast.error(response.errstr || '添加失败');
      }
    } catch (error) {
      console.error('添加失败:', error);
      Toast.error('添加失败');
    }
  };

  const handleEdit = async (values) => {
    // 数据预处理
    const processedValues = {
      ...values,
      // 确保数值类型字段为整数
      gender: parseInt(values.gender),
      status: parseInt(values.status),
      from_su_uid: values.from_su_uid ? parseInt(values.from_su_uid) : '',
      // 处理生日格式，确保日期格式有效
      birthday: values.birthday ? (() => {
        const dateObj = new Date(values.birthday);
        if (isNaN(dateObj.getTime())) return 0;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return parseInt(`${year}${month}${day}`);
      })() : 0
    };

    try {
      const response = await request.post('/go/sp/su/edit_service_user/', {
        ...processedValues,
        uid: currentRecord.uid
      });
      if (response.errno === 0) {
        Toast.success('编辑成功');
        setShowEditModal(false);
        fetchList();
      } else {
        Toast.error(response.errstr || '编辑失败');
      }
    } catch (error) {
      console.error('编辑失败:', error);
      Toast.error('编辑失败');
    }
  };

  const handleDelete = async (uids) => {
    try {
      const response = await request.post('/go/sp/su/delete_service_user/', {
        uids: Array.isArray(uids) ? uids.join(',') : uids
      });
      if (response.errno === 0) {
        Toast.success('删除成功');
        setSelectedRowKeys([]);
        fetchList();
      } else {
        Toast.error(response.errstr || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      Toast.error('删除失败');
    }
  };

  const handleBatchEdit = async (status, uids) => {
    if (!uids || (Array.isArray(uids) && !uids.length)) {
      Toast.warning('请选择要操作的用户');
      return;
    }
    try {
      const response = await request.post('/go/sp/su/batchedit_service_user/', {
        uids: Array.isArray(uids) ? uids.join(',') : uids,
        status
      });
      if (response.errno === 0) {
        Toast.success('操作成功');
        setSelectedRowKeys([]);
        fetchList();
      } else {
        Toast.error(response.errstr || '操作失败');
      }
    } catch (error) {
      console.error('操作失败:', error);
      Toast.error('操作失败');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString();
  };

  const formatBirthday = (birthday) => {
    if (!birthday) return '-';
    const str = birthday.toString();
    return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
  };

  const columns = [
    {
      title: '用户信息',
      dataIndex: 'account',
      width: 200,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Avatar size="small" src={record.avatar}>{record.name?.[0] || text?.[0] || '?'}</Avatar>
          <div style={{ fontSize: '12px' }}>
            <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text || '-'}</div>
            <div style={{ color: 'var(--semi-color-text-2)' }}>{record.name}</div>
            {record.sp_remark && <div
              style={{
                color: 'var(--semi-color-danger)',
                marginTop: '4px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
              onClick={() => {
                Modal.confirm({
                  title: '编辑备注',
                  content: (
                    <Input
                      defaultValue={record.sp_remark}
                      onChange={value => window.editRemark = value}
                      placeholder="请输入备注"
                      style={{ width: '100%' }}
                      autoFocus
                    />
                  ),
                  onOk: async () => {
                    if (window.editRemark !== undefined) {
                      try {
                        const response = await request.post('/go/sp/su/batcheditremark_service_user/', {
                          uids: record.uid,
                          sp_remark: window.editRemark
                        });
                        if (response.errno === 0) {
                          Toast.success('更新备注成功');
                          fetchList();
                        } else {
                          Toast.error(response.errstr || '更新备注失败');
                        }
                      } catch (error) {
                        console.error('更新备注失败:', error);
                        Toast.error('更新备注失败');
                      }
                    }
                  }
                });
              }}
            >{record.sp_remark}</div>}
            {!record.sp_remark && <div
              className="show-on-hover"
              style={{
                color: 'var(--semi-color-text-2)',
                marginTop: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onClick={() => {
                Modal.confirm({
                  title: '添加备注',
                  content: (
                    <Input
                      onChange={value => window.editRemark = value}
                      placeholder="请输入备注"
                      style={{ width: '100%' }}
                      autoFocus
                    />
                  ),
                  onOk: async () => {
                    if (window.editRemark !== undefined) {
                      try {
                        const response = await request.post('/go/sp/su/batcheditremark_service_user/', {
                          uids: record.uid,
                          sp_remark: window.editRemark
                        });
                        if (response.errno === 0) {
                          Toast.success('添加备注成功');
                          fetchList();
                        } else {
                          Toast.error(response.errstr || '添加备注失败');
                        }
                      } catch (error) {
                        console.error('添加备注失败:', error);
                        Toast.error('添加备注失败');
                      }
                    }
                  }
                });
              }}
            >
              <IconEdit />
              <span>添加备注</span>
            </div>}
          </div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (text) => {
        const status = statusMap[text] || { text: '异常', type: 'danger' };
        return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
      }
    },
    {
      title: '基本信息',
      dataIndex: 'gender',
      width: 200,
      render: (text, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>性别：{genderMap[text] || '-'}</div>
          <div>生日：{formatBirthday(record.birthday)}</div>
          <div>微信：{record.wechat || '-'}</div>
        </div>
      )
    },
    {
      title: '来源信息',
      dataIndex: 'channel',
      width: 200,
      render: (text, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>渠道：{text || '-'}</div>
          <div>推荐人ID：{record.from_su_uid || '-'}</div>
          <div>最后IP：{record.last_ip || '-'}</div>
        </div>
      )
    },
    {
      title: '创建时间/更新时间',
      dataIndex: 'update_time',
      width: 180,
      render: (text, record) => (
        <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
          <div>{formatTimestamp(record.create_time)}</div>
          <div>{formatTimestamp(text)}</div>
        </div>
      )
    },
    {
      title: '操作',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type='tertiary'
            icon={<IconEyeOpened />}
            onClick={() => navigate(`/user/${record.uid}`)}
          >
            详情
          </Button>
          <Button
            type='primary'
            icon={<IconEdit />}
            onClick={() => {
              setCurrentRecord(record);
              setShowEditModal(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除该用户吗？"
            onConfirm={() => handleDelete(record.uid)}
          >
            <Button type='danger' icon={<IconDelete />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.usersContainer}>
      <div className={styles.header}>
        <Space>
          <Input
            placeholder="搜索账号/用户名"
            value={searchKey}
            onChange={setSearchKey}
            style={{ width: 200 }}
            showClear
          />
          <Select
            placeholder="用户状态"
            value={searchStatus}
            onChange={setSearchStatus}
            style={{ width: 150 }}
            showClear
          >
            <Select.Option value={null}>全部状态</Select.Option>
            {Object.entries(statusMap).map(([value, { text }]) => (
              <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={() => fetchList(1, pagination.pageSize)}
            loading={loading}
            icon={<IconSearch />}
          >
            搜索
          </Button>
          <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增用户</Button>
          {selectedRowKeys.length > 0 && (
            <Space>
              <Button type='warning' icon={<IconStop />} onClick={() => {
                Modal.confirm({
                  title: '确认封号',
                  content: `确认要封禁选中的 ${selectedRowKeys.length} 个用户吗？`,
                  okText: '确定',
                  cancelText: '取消',
                  onOk: () => handleBatchEdit(20, selectedRowKeys)
                });
              }}>批量封号</Button>
              <Button type='primary' icon={<IconPlayCircle />} onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量解封</Button>
              <Button
                type='primary'
                onClick={() => {
                  Modal.confirm({
                    title: '批量编辑备注',
                    content: (
                      <Input
                        onChange={value => window.batchRemark = value}
                        placeholder="请输入备注"
                      />
                    ),
                    onOk: async () => {
                      if (window.batchRemark !== undefined) {
                        try {
                          const response = await request.post('/go/sp/su/batcheditremark_service_user/', {
                            uids: selectedRowKeys.join(','),
                            sp_remark: window.batchRemark
                          });
                          if (response.errno === 0) {
                            Toast.success('批量更新备注成功');
                            fetchList();
                          } else {
                            Toast.error(response.errstr || '批量更新备注失败');
                          }
                        } catch (error) {
                          console.error('批量更新备注失败:', error);
                          Toast.error('批量更新备注失败');
                        }
                      }
                    }
                  });
                }}
              >
                批量编辑备注
              </Button>
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个用户吗？`}
                onConfirm={() => handleDelete(selectedRowKeys)}
              >
                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
              </Popconfirm>
            </Space>
          )}
        </Space>
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: (page) => fetchList(page, pagination.pageSize),
          onPageSizeChange: (size) => fetchList(1, size)
        }}
        rowKey="uid"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys
        }}
      />

      <Modal
        title="新增用户"
        visible={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        centered
      >
        <Form onSubmit={handleAdd}>
          <Form.Input
            field="account"
            label="账号"
            placeholder="请输入手机号码"
          />
          <Form.Input
            field="name"
            label="用户名"
            placeholder="请输入用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          />
          <Form.Select
            field="gender"
            label="性别"
            initValue={1}
          >
            {Object.entries(genderMap).map(([value, text]) => (
              <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
            ))}
          </Form.Select>
          <Form.DatePicker
            field="birthday"
            label="生日"
            placeholder="请选择生日"
            format="yyyy-MM-dd"
            type="date"
            onChange={(date) => {
              if (date) {
                const dateObj = new Date(date);
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                return parseInt(`${year}${month}${day}`);
              }
              return null;
            }}
          />
          <Form.Input
            field="wechat"
            label="微信号"
            placeholder="请输入微信号"
          />
          <Form.Input
            field="from_su_uid"
            label="推荐人ID"
            placeholder="请输入推荐人ID"
          />
          <Form.Input
            field="channel"
            label="来源渠道"
            placeholder="请输入来源渠道"
          />
          <Form.Select
            field="status"
            label="状态"
            initValue={0}
          >
            {Object.entries(statusMap).map(([value, { text }]) => (
              <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
            ))}
          </Form.Select>
          <Form.Input
            field="sp_remark"
            label="备注"
            placeholder="请输入备注"
          />
          <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
            <Space>
              <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
              <Button type='primary' htmlType='submit'>确定</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <Modal
        title="编辑用户"
        visible={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        centered
      >
        <Form
          onSubmit={handleEdit}
          initValues={{
            name: currentRecord?.name,
            gender: currentRecord?.gender,
            birthday: currentRecord?.birthday ? (() => {
              const str = currentRecord.birthday.toString();
              return new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`);
            })() : null,
            wechat: currentRecord?.wechat,
            from_su_uid: currentRecord?.from_su_uid,
            channel: currentRecord?.channel,
            status: currentRecord?.status,
            sp_remark: currentRecord?.sp_remark
          }}
        >
          <Form.Input
            field="name"
            label="用户名"
            placeholder="请输入用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          />
          <Form.Select
            field="gender"
            label="性别"
          >
            {Object.entries(genderMap).map(([value, text]) => (
              <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
            ))}
          </Form.Select>
          <Form.DatePicker
            field="birthday"
            label="生日"
            placeholder="请选择生日"
            format="yyyy-MM-dd"
            type="date"
            onChange={(date) => {
              if (date) {
                const dateObj = new Date(date);
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                return parseInt(`${year}${month}${day}`);
              }
              return null;
            }}
          />
          <Form.Input
            field="wechat"
            label="微信号"
            placeholder="请输入微信号"
          />
          <Form.Input
            field="from_su_uid"
            label="推荐人ID"
            placeholder="请输入推荐人ID"
          />
          <Form.Input
            field="channel"
            label="来源渠道"
            placeholder="请输入来源渠道"
          />
          <Form.Select
            field="status"
            label="状态"
          >
            {Object.entries(statusMap).map(([value, { text }]) => (
              <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
            ))}
          </Form.Select>
          <Form.Input
            field="sp_remark"
            label="备注"
            placeholder="请输入备注"
          />
          <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
            <Space>
              <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
              <Button type='primary' htmlType='submit'>确定</Button>
            </Space>
          </div>
        </Form>
      </Modal>


    </div>
  );
};

export default Users;